/* eslint-disable */

import React, { useState, useRef, useEffect } from 'react'
import { Box, Paper, Typography, Divider, ToggleButtonGroup, ToggleButton, IconButton, Button, Slider } from '@mui/material'
import { SwatchesPicker, ColorResult } from 'react-color'
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PaletteIcon from '@mui/icons-material/Palette';

interface ColorChartControlsProps {
  width: number;
  height: number;
  zoom: number;
  color: string;
  onWidthChange: (value: number) => void;
  onHeightChange: (value: number) => void;
  onZoomChange: (value: number) => void;
  onColorChange: (color: ColorResult) => void;
  onFitToView: () => void;
  maxWidth?: number;
  maxHeight?: number;
}

export default function ColorChartControls({
  width,
  height,
  zoom,
  color,
  onWidthChange,
  onHeightChange,
  onZoomChange,
  onColorChange,
  onFitToView,
  maxWidth = 100,
  maxHeight = 100
}: ColorChartControlsProps) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (colorPickerOpen && colorPickerRef.current && paperRef.current) {
      // Scroll the Paper container to show the color picker
      const paperElement = paperRef.current;
      const colorPickerElement = colorPickerRef.current;
      
      setTimeout(() => {
        colorPickerElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 100);
    }
  }, [colorPickerOpen]);

  return (
    <Paper 
      ref={paperRef}
      elevation={1} 
      sx={{ 
        padding: 1, 
        height: '100%', 
        maxHeight: { xs: '50vh', md: '100%' },
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0
      }}>
      {/* Scrollable Content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        pt: 2,
        pb: 1,
        px: 1
      }}>
        {/* Dimensions */}
        <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
          Dimensions
        </Typography>
        <Box sx={{ mb: 0.75, minHeight: '48px', pt: 0.5, px: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.125, fontSize: '0.75rem' }}>
            Height: {height}
          </Typography>
          <Slider
            value={height}
            min={1}
            max={maxHeight}
            valueLabelDisplay="auto"
            onChange={(e, val) => onHeightChange(val as number)}
            size="small"
            sx={{ mt: 0.25 }}
          />
        </Box>
        <Box sx={{ minHeight: '48px', pt: 0.5, px: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.125, fontSize: '0.75rem' }}>
            Width: {width}
          </Typography>
          <Slider
            value={width}
            min={1}
            max={maxWidth}
            valueLabelDisplay="auto"
            onChange={(e, val) => onWidthChange(val as number)}
            size="small"
            sx={{ mt: 0.25 }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 0.5, flexShrink: 0 }} />

      {/* Zoom Controls */}
      <Box sx={{ mb: 1, flexShrink: 0, pt: 0.5, px: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.25, fontSize: '0.85rem' }}>
          Zoom: {Math.round(zoom * 100)}%
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <IconButton 
            size="small" 
            onClick={() => onZoomChange(Math.max(0.1, zoom - 0.1))}
            disabled={zoom <= 0.1}
            sx={{ padding: 0.25 }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Slider
            value={zoom}
            min={0.1}
            max={1}
            step={0.1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            onChange={(e, val) => onZoomChange(val as number)}
            sx={{ flexGrow: 1 }}
            size="small"
          />
          <IconButton 
            size="small" 
            onClick={() => onZoomChange(Math.min(1, zoom + 0.1))}
            disabled={zoom >= 1}
            sx={{ padding: 0.25 }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Box>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<FitScreenIcon fontSize="small" />}
          onClick={onFitToView}
          sx={{ py: 0.25, fontSize: '0.75rem' }}
        >
          Fit to View
        </Button>
      </Box>

      <Divider sx={{ my: 0.75, flexShrink: 0 }} />

      {/* Color Picker - Sticky at bottom */}
      <Box 
        ref={colorPickerRef}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          pt: 0.5,
          pb: 0,
          scrollMarginTop: '1rem',
          zIndex: 1
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 0.25,
          backgroundColor: colorPickerOpen ? 'rgba(90, 74, 66, 0.05)' : 'transparent',
          padding: colorPickerOpen ? '0.375rem' : '0',
          borderRadius: colorPickerOpen ? '4px' : '0',
          transition: 'all 0.2s ease'
        }}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Color Picker
          </Typography>
          <Button
            size="small"
            onClick={() => setColorPickerOpen(!colorPickerOpen)}
            sx={{ 
              display: { xs: 'flex', md: 'none' },
              minWidth: 'auto',
              px: 1,
              py: 0.25,
              backgroundColor: colorPickerOpen ? 'primary.main' : 'transparent',
              color: colorPickerOpen ? 'white' : 'primary.main',
              '&:hover': {
                backgroundColor: colorPickerOpen ? 'primary.dark' : 'rgba(90, 74, 66, 0.1)'
              }
            }}
            startIcon={<PaletteIcon fontSize="small" />}
            endIcon={colorPickerOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          >
            {colorPickerOpen ? 'Hide' : 'Show'}
          </Button>
        </Box>
        <Box sx={{ 
          mt: 1, 
          display: { xs: colorPickerOpen ? 'block' : 'none', md: 'block' },
          animation: colorPickerOpen ? 'fadeIn 0.3s ease-in' : 'none',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(-10px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        }}>
          <SwatchesPicker
            width="100%"
            height="400px"
            color={color}
            onChange={onColorChange}
          />
        </Box>
      </Box>
      </Box>
    </Paper>
  )
}

